import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, AlertTriangle, Image as ImgIcon } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["submitted", "under_review", "approved", "rejected", "paid"];

export default function AdminSubmissionDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [finalPayout, setFinalPayout] = useState("");
  const [txnId, setTxnId] = useState("");

  const load = () => {
    api.get(`/admin/submissions/${id}`).then((r) => {
      setSub(r.data);
      setAdminNotes(r.data.admin_notes || "");
      setFinalPayout(r.data.final_payout ?? r.data.estimated_payout);
    });
  };

  useEffect(load, [id]);

  const changeStatus = async (status) => {
    try {
      await api.post(`/admin/submissions/${id}/status`, {
        status,
        admin_notes: adminNotes,
        final_payout: Number(finalPayout) || null,
      });
      toast.success(`Status updated to ${status}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const markPaid = async () => {
    if (!txnId) return toast.error("Enter transaction ID");
    try {
      await api.post(`/admin/submissions/${id}/mark-paid`, {
        transaction_id: txnId,
        final_payout: Number(finalPayout) || 0,
        note: adminNotes,
      });
      toast.success("Marked as paid");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  if (!sub) return <div className="p-8 text-[#9CA3AF]">Loading…</div>;

  return (
    <div className="p-6 md:p-8" data-testid="admin-sub-detail">
      <button
        onClick={() => nav("/admin/submissions")}
        className="inline-flex items-center gap-1 text-sm text-[#9CA3AF] hover:text-[#0A1628] mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono-display font-bold text-2xl text-[#0A1628]">{sub.reference}</div>
          <div className="text-sm text-[#9CA3AF]">Created {new Date(sub.created_at).toLocaleString()}</div>
        </div>
        <span className="inline-block px-3 py-1.5 rounded-full text-xs font-semibold uppercase bg-[#0A1628] text-white">
          {sub.status.replace("_", " ")}
        </span>
      </div>

      {sub.fraud_flags?.length > 0 && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex items-start gap-2 mb-5 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Fraud signals:</strong> {sub.fraud_flags.join(", ")}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <Panel title="Customer">
            <KV k="Name" v={sub.full_name} />
            <KV k="Email" v={sub.email} />
            {sub.phone && <KV k="Phone" v={sub.phone} />}
            <KV k="IP" v={sub.submitter_ip || "—"} mono />
          </Panel>
          <Panel title="Cards">
            <ul className="space-y-3">
              {sub.cards.map((c, i) => (
                <li key={i} className="border border-[#E5E7EB] rounded-lg p-4" data-testid={`admin-card-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-[#0A1628]">{c.brand_name}</div>
                    <div className="font-mono-display text-sm text-[#0052FF] font-semibold">
                      ${c.estimated_payout.toFixed(2)} ({c.rate}%)
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                    <KV k="Balance" v={`$${c.balance.toFixed(2)}`} mono />
                    <KV k="Card #" v={c.card_number_masked} mono />
                    <KV k="PIN" v={c.pin || "—"} mono />
                  </div>
                  {c.image_paths?.length > 0 && (
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      {c.image_paths.map((p) => (
                        <AdminImage key={p} path={p} />
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </Panel>
          <Panel title="Timeline">
            <ul className="space-y-2 text-sm">
              {sub.timeline.map((t) => (
                <li key={t.id} className="flex items-center justify-between">
                  <div>
                    <span className="capitalize font-medium text-[#0A1628]">{t.status.replace("_", " ")}</span>
                    {t.note && <span className="text-[#9CA3AF] ml-2">— {t.note}</span>}
                  </div>
                  <span className="text-xs text-[#9CA3AF] font-mono-display">{new Date(t.timestamp).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </Panel>
        </div>
        <div className="space-y-5">
          <Panel title="Payout">
            <KV k="Method" v={sub.payout_method.toUpperCase()} />
            <KV k="Handle" v={sub.payout_handle} mono />
            <KV k="Estimated" v={`$${sub.estimated_payout.toFixed(2)}`} mono />
            {sub.final_payout != null && <KV k="Final" v={`$${sub.final_payout.toFixed(2)}`} mono />}
          </Panel>
          <Panel title="Admin actions">
            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">Admin notes</span>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
                data-testid="admin-notes-input"
                className="w-full p-2.5 rounded-lg border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] resize-none"
              />
            </label>
            <label className="block mt-3">
              <span className="block text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">Final payout</span>
              <input
                type="number"
                value={finalPayout}
                onChange={(e) => setFinalPayout(e.target.value)}
                data-testid="admin-final-payout"
                className="w-full h-10 px-3 rounded-lg border border-[#E5E7EB] font-mono-display text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF]"
              />
            </label>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {STATUSES.filter((s) => s !== "paid").map((s) => (
                <button
                  key={s}
                  onClick={() => changeStatus(s)}
                  data-testid={`admin-status-${s}`}
                  className="h-9 rounded-lg text-xs font-semibold capitalize border border-[#E5E7EB] hover:border-[#0A1628] text-[#0A1628]"
                >
                  {s.replace("_", " ")}
                </button>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">Transaction ID</span>
                <input
                  value={txnId}
                  onChange={(e) => setTxnId(e.target.value)}
                  data-testid="admin-txn-id"
                  placeholder="paypal-xxx-xxx"
                  className="w-full h-10 px-3 rounded-lg border border-[#E5E7EB] font-mono-display text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF]"
                />
              </label>
              <button
                onClick={markPaid}
                data-testid="admin-mark-paid"
                className="mt-3 w-full h-10 rounded-lg bg-[#10B981] text-white font-semibold hover:bg-emerald-600"
              >
                Mark as paid
              </button>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <section className="bg-white border border-[#E5E7EB] rounded-2xl p-5 soft-shadow">
      <h3 className="font-display font-semibold text-[#0A1628] mb-3">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

function KV({ k, v, mono }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-[#9CA3AF]">{k}</span>
      <span className={`text-[#0A1628] ${mono ? "font-mono-display" : ""}`}>{v}</span>
    </div>
  );
}

function AdminImage({ path }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null;
    const token = localStorage.getItem("gf_admin_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((b) => {
        url = URL.createObjectURL(b);
        setSrc(url);
      });
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [path]);
  if (!src)
    return (
      <div className="aspect-square bg-[#F3F4F6] rounded-lg flex items-center justify-center">
        <ImgIcon className="w-5 h-5 text-[#9CA3AF]" />
      </div>
    );
  return (
    <a href={src} target="_blank" rel="noreferrer" className="block">
      <img src={src} className="aspect-square object-cover rounded-lg border border-[#E5E7EB]" alt="card" />
    </a>
  );
}
